// This function sends out an email to the farm collecting the order.  If an
// order contains multiple farms then this function will be called for each farm.
// It calls the cloud function sendMail to complete this task.
import firebase from "../../components/Firebase.js";

function hasOrderItems([farm, food, userInfo]) {
  return food.some(
    (orderItem) =>
      userInfo.userType === "individual" ||
      orderItem.farmName === farm.farmName,
  );
}

export async function sendOrderEmail(item) {
  if (!hasOrderItems(item)) return null;
  const [
    farm,
    food,
    userInfo,
    deliveryDate,
    grandTotal,
    donationSubsidy,
    customerContribution,
  ] = item;
  const [deliveryFee, cashPortion, creditPortion, costTotal] = item.slice(9);
  // These are the list of items this farm will be providing
  const farmOrders = [];
  // The stringified version of order
  let orderString = "";
  // The package fees in total for the order.
  let packageFeeTotal = 0;
  // The package fees owed for the order in string format to be put in the email.
  let packageFeeString = "";
  // The delivery fees owed for the order in string format to be put in the email.
  let deliveryFeeString = "";
  // The volunteer fees owed for the order in string format to be put in the email.
  let volunteerFeeString = "";

  // Individual orders should include which farmers are selling which products so we
  // create the list to be a dictionary with farm keys and the food as an array.
  const individalFarmOrders = {};

  // filter out the items for that aren't ordered from the current farm and create
  // a list of these food items.
  if (userInfo.userType !== "individual") {
    food.forEach((orderItem) => {
      if (orderItem.farmName === farm.farmName) {
        farmOrders.push(orderItem);
      }
    });
  } else {
    // cycle through the food and add the item to a dictionary with the farm as the key
    // with the value being an array of the list of items.
    food.forEach((orderItem) => {
      if (individalFarmOrders[orderItem.farmName]) {
        individalFarmOrders[orderItem.farmName].push(orderItem);
      } else {
        individalFarmOrders[orderItem.farmName] = [orderItem];
      }
    });
  }

  if (userInfo.userType === "individual") {
    // Cycle through the farms that the individal ordered from.
    Object.keys(individalFarmOrders).forEach((farmName) => {
      // Add the name of the farm.
      orderString = orderString.concat(
        '<span style="font-size: 14px;"><u><b>',
        farmName.toString(),
        ":</b></u></span><br />",
      );
      // Cycle through the items from the farm.
      individalFarmOrders[farmName].forEach((orderItem) => {
        // The order item price that will be used it may be reset to 0 if the
        // suggested price is true.
        let orderItemPrice = orderItem.price;
        // The description of the orderItem that is used to let the user know how much they
        // are buying.
        let description = orderItem.individualDescription;

        // Set the price of the individal item
        let individualPrice = parseFloat(
          orderItemPrice /
            (orderItem.distributionQuantity / orderItem.individualQuantity),
        );

        // if suggestedPrice is true then we want to set the price to 0
        if (orderItem.suggestedPrice) {
          // First add to the description the suggested Donation.
          description = description.concat(
            " - Suggested Donation of $",
            individualPrice.toFixed(2),
          );
          // Then reset the prices to 0.
          orderItemPrice = 0.0;
          individualPrice = 0.0;
        }

        // If there is a package fee then add it to the item price and add it to the
        // the total package fee.
        if (orderItem.packageFee > 0) {
          individualPrice += parseFloat(orderItem.packageFee);
          packageFeeTotal +=
            parseFloat(orderItem.packageFee) * parseFloat(orderItem.quantity);
        }
        // Set the price of the individal item multiplied by the amount of that item.
        const individualPriceTotal = parseFloat(
          individualPrice * orderItem.quantity,
        ).toFixed(2);
        // Make the price a 2 decimal float for dollar format.
        individualPrice = individualPrice.toFixed(2);
        // Create the string for the email with the order item.
        orderString = orderString.concat(
          "• ",
          orderItem.item.toString(),
          " ",
          description.toString(),
          " ",
          " x",
          orderItem.quantity.toString(),
          " -- $",
          individualPrice,
          " ($",
          individualPriceTotal.toString(),
          ")",
        );
        // If there is a package fee then add it to the string to let the user know.
        if (orderItem.packageFee > 0) {
          orderString = orderString.concat(
            " *includes pkg fee of ",
            parseFloat(orderItem.packageFee).toFixed(2),
            "/per item.",
          );
        }
        orderString = orderString.concat("<br />");
      });
    });
    // If the packageFee is greater than 0 then we want to print the total fees.
    if (packageFeeTotal > 0) {
      packageFeeString = packageFeeString.concat(
        "+ Package Fees : $",
        packageFeeTotal.toFixed(2),
        "<br />",
      );
    }

    // If the deliveryFee is greater than 0 then we want to print the total fees.
    if (deliveryFee > 0) {
      deliveryFeeString = deliveryFeeString.concat(
        "+ Delivery Fee : $",
        deliveryFee.toFixed(2),
        "**<br />",
      );
    }

    // If the volunteer cash portion is greater than 0 then we want to print the total fees.
    if (cashPortion > 0) {
      volunteerFeeString = volunteerFeeString.concat(
        "+ Participation Fee Cash : $",
        cashPortion,
        "<br />",
      );
    }
    // If the volunteer credit portion is greater than 0 then we want to print the total fees.
    if (creditPortion > 0) {
      volunteerFeeString = volunteerFeeString.concat(
        "Participation Fee Credits : ",
        creditPortion,
        " credits<br />",
      );
    }
  } else if (userInfo.userType === "distributionLocation") {
    // Group rows by hub and fall back to Pickup On Site if no hub breakdown is provided.
    const hubOrders = {};
    const hubDisplayOrder = [];
    const pickupOnSite = "General";

    // Helper to keep hub headings in insertion order.
    const registerHub = (hubName) => {
      if (hubOrders[hubName] === undefined) {
        hubOrders[hubName] = [];
        hubDisplayOrder.push(hubName);
      }
    };

    farmOrders.forEach((orderItem) => {
      // The order item price that will be used it may be reset to 0 if the
      // suggested price is true.
      let orderItemPrice = orderItem.price;
      if (orderItem.suggestedPrice) {
        orderItemPrice = 0.0;
      }

      const hasHubBreakdown =
        orderItem.communityHubQuantities &&
        typeof orderItem.communityHubQuantities === "object" &&
        Object.keys(orderItem.communityHubQuantities).length > 0;

      if (!hasHubBreakdown) {
        registerHub(pickupOnSite);
        // The total of the items multiplied by the quantity the user is purchasing.
        const priceTotal = parseFloat(
          orderItemPrice * orderItem.quantity,
        ).toFixed(2);
        hubOrders[pickupOnSite].push(
          "• " +
            orderItem.item.toString() +
            " " +
            orderItem.description.toString() +
            " " +
            " x" +
            orderItem.quantity.toString() +
            " -- $" +
            parseFloat(orderItemPrice).toFixed(2).toString() +
            " ($" +
            priceTotal.toString() +
            ") <br />",
        );
      } else {
        Object.keys(orderItem.communityHubQuantities).forEach((hubName) => {
          const hubQuantity = orderItem.communityHubQuantities[hubName];
          if (!hubQuantity || parseFloat(hubQuantity) <= 0) {
            return;
          }
          registerHub(hubName);
          // The total of the items multiplied by the amount sent to this hub.
          const priceTotal = parseFloat(orderItemPrice * hubQuantity).toFixed(
            2,
          );
          hubOrders[hubName].push(
            "• " +
              orderItem.item.toString() +
              " " +
              orderItem.description.toString() +
              " " +
              " x" +
              hubQuantity.toString() +
              " -- $" +
              parseFloat(orderItemPrice).toFixed(2).toString() +
              " ($" +
              priceTotal.toString() +
              ") <br />",
          );
        });
      }
    });

    // Put Pickup On Site last if it exists.
    const orderedHubs = hubDisplayOrder.filter(
      (hubName) => hubName !== pickupOnSite,
    );
    if (hubDisplayOrder.includes(pickupOnSite)) {
      orderedHubs.push(pickupOnSite);
    }

    orderedHubs.forEach((hubName) => {
      if (hubOrders[hubName].length === 0) {
        return;
      }
      orderString = orderString.concat("<b>", hubName.toString(), "</b><br />");
      hubOrders[hubName].forEach((line) => {
        orderString = orderString.concat(line);
      });
      orderString = orderString.concat("<br />");
    });
  } else {
    // convert the orders in this farms order array into a string to be sent in the email.
    farmOrders.forEach((orderItem) => {
      // The order item price that will be used it may be reset to 0 if the
      // suggested price is true.
      let orderItemPrice = orderItem.price;

      // if suggestedPrice is true then we want to set the price to 0
      if (orderItem.suggestedPrice) {
        // Then reset the prices to 0.
        orderItemPrice = 0.0;
      }

      // The total of the items multiplied by the quantity the user is purchasing.
      const priceTotal = parseFloat(
        orderItemPrice * orderItem.quantity,
      ).toFixed(2);
      orderString = orderString.concat(
        "• ",
        orderItem.item.toString(),
        " ",
        orderItem.description.toString(),
        " ",
        " x",
        orderItem.quantity.toString(),
        " -- $",
        parseFloat(orderItemPrice).toFixed(2).toString(),
        " ($",
        priceTotal.toString(),
        ") <br />",
      );
    });
  }

  // If this is not an individual than add the farms to the order.  If this is an
  // individual than at their pickupLocation's email to the marketplace.
  let dest = [userInfo.email];
  if (userInfo.userType === "individual") {
    dest.push(userInfo.pickupLocation.email);
  } else {
    dest = [farm.farmerEmail];
  }

  const sendMail = firebase.functions().httpsCallable("sendMail");
  await sendMail({
    dest,
    userInfo,
    farm,
    order: orderString,
    deliveryDate,
    grandTotal,
    donationSubsidy,
    customerContribution,
    packageFeeString,
    deliveryFeeString,
    volunteerFeeString,
    costTotal,
  });
}

// Legacy vendor callers still own their progress and email-error dialog here.
export default async function OrderEmails(params) {
  if (!hasOrderItems(params)) return null;
  const setPlacingOrder = params[7];
  const setSnackBarOpen = params[8];
  setPlacingOrder(true);
  try {
    await sendOrderEmail(params);
    setSnackBarOpen?.(true);
  } catch (error) {
    console.error("There was an error when calling the Cloud Function", error);
    window.alert(
      `There was an error when calling the Cloud Function:\n\nError Code: ${error.code}\nError Message: ${error.message}`,
    );
    throw error;
  } finally {
    setPlacingOrder(false);
  }
}
